import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  QueryStateBoundary,
} from "@/components/ui";
import { centsToDollarsInput, centsToUsd, dollarsToCents } from "@/lib/money";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useInvoices } from "@/api/hooks/useInvoices";
import { useContribution, useSetEventCosts } from "@/api/hooks/useContribution";
import {
  INVOICE_KIND_BADGE_VARIANT,
  INVOICE_KIND_LABELS,
  INVOICE_STATUS_BADGE_VARIANT,
  INVOICE_STATUS_LABELS,
  paidCents,
  type Invoice,
} from "@/api/types/invoice";
import { DEFAULT_EVENT_COSTS_CENTS, costsAreUnset } from "@/api/types/contribution";
import type { Event } from "@/api/types/event";
import { RecordPaymentDialog } from "@/features/money/RecordPaymentDialog";

/**
 * Phase 5's event-facing money section: the event's deposit/balance/addon
 * invoices with a quick "Record payment" action, the event-costs form, and
 * the $750-contribution scoreboard. Additive to EventDetailPage — sits
 * alongside (not in place of) the Phase 3 MoneyCard, which tracks the
 * event's own deposit_paid_at/balance_paid_at markers via a separate
 * mechanism. See builder report for why both coexist.
 */
export function EventMoneySection({ event }: { event: Event }) {
  return (
    <>
      <EventInvoicesCard eventId={event.id} />
      <EventCostsAndContribution eventId={event.id} />
    </>
  );
}

// ---- Invoices ---------------------------------------------------------

function EventInvoicesCard({ eventId }: { eventId: string }) {
  const { data, isLoading, error, refetch } = useInvoices({ event_id: eventId });
  const invoices = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const [payInvoiceId, setPayInvoiceId] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoices</CardTitle>
      </CardHeader>
      <CardContent>
        <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
          {invoices.length === 0 ? (
            <p className="text-sm text-text-muted" data-testid="event-invoices-empty">
              No invoices yet.
            </p>
          ) : (
            <ul className="divide-y divide-border" data-testid="event-invoices-list">
              {invoices.map((invoice) => (
                <EventInvoiceRow
                  key={invoice.id}
                  invoice={invoice}
                  onPay={() => setPayInvoiceId(invoice.id)}
                />
              ))}
            </ul>
          )}
        </QueryStateBoundary>
      </CardContent>

      {payInvoiceId && (
        <RecordPaymentDialog
          open={!!payInvoiceId}
          onClose={() => setPayInvoiceId(null)}
          invoiceId={payInvoiceId}
        />
      )}
    </Card>
  );
}

function EventInvoiceRow({ invoice, onPay }: { invoice: Invoice; onPay: () => void }) {
  const canPay = invoice.status !== "void" && invoice.status !== "paid";

  return (
    <li
      className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
      data-invoice-id={invoice.id}
    >
      <div>
        <p className="font-medium text-text-primary">
          {invoice.number}
          <Badge variant={INVOICE_KIND_BADGE_VARIANT[invoice.kind]} size="sm" className="ml-2">
            {INVOICE_KIND_LABELS[invoice.kind]}
          </Badge>
        </p>
        <p className="text-xs text-text-muted">
          {centsToUsd(invoice.total_cents)} total · {centsToUsd(paidCents(invoice))} paid
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={INVOICE_STATUS_BADGE_VARIANT[invoice.status]} data-testid={`event-invoice-status-${invoice.id}`}>
          {INVOICE_STATUS_LABELS[invoice.status]}
        </Badge>
        {canPay && (
          <Button size="sm" onClick={onPay}>
            Record payment
          </Button>
        )}
        <a
          href={`/money/${invoice.id}`}
          className="text-sm font-medium text-primary hover:underline"
        >
          View
        </a>
      </div>
    </li>
  );
}

// ---- Costs + contribution ------------------------------------------------

/**
 * Shares a single useContribution() call for both the costs form (which
 * prefills from `costs`) and the contribution scoreboard (revenue/total
 * costs/delta) so they always agree and React Query dedupes the request.
 */
function EventCostsAndContribution({ eventId }: { eventId: string }) {
  const { data: contribution, isLoading, error, refetch } = useContribution(eventId);

  return (
    <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      {contribution && (
        <>
          <EventCostsCard eventId={eventId} costs={contribution.costs} />
          <ContributionCard contribution={contribution} />
        </>
      )}
    </QueryStateBoundary>
  );
}

interface EventCostsCardProps {
  eventId: string;
  costs: {
    second_bartender_cents: number;
    consumables_cents: number;
    shooter_cents: number;
    fuel_cents: number;
    other_cents: number;
    notes?: string | null;
  };
}

const COST_FIELDS = [
  { key: "second_bartender_cents", label: "Second bartender" },
  { key: "consumables_cents", label: "Consumables" },
  { key: "shooter_cents", label: "Shooter" },
  { key: "fuel_cents", label: "Fuel" },
  { key: "other_cents", label: "Other" },
] as const;

function EventCostsCard({ eventId, costs }: EventCostsCardProps) {
  const setCosts = useSetEventCosts();

  // "None saved" = every field still at zero — show the playbook defaults
  // as the starting point instead of a blank/zeroed form (see
  // types/contribution.ts's costsAreUnset for the ambiguity this resolves).
  const initial = costsAreUnset(costs) ? DEFAULT_EVENT_COSTS_CENTS : costs;

  const [values, setValues] = useState<Record<(typeof COST_FIELDS)[number]["key"], string>>({
    second_bartender_cents: centsToDollarsInput(initial.second_bartender_cents),
    consumables_cents: centsToDollarsInput(initial.consumables_cents),
    shooter_cents: centsToDollarsInput(initial.shooter_cents),
    fuel_cents: centsToDollarsInput(initial.fuel_cents),
    other_cents: centsToDollarsInput(initial.other_cents),
  });

  // Re-sync display values if the server's saved costs change out from under
  // us (e.g. another tab saved), but only before the user has touched the
  // form in this session's initial render — keyed off eventId so switching
  // events resets it.
  useEffect(() => {
    setValues({
      second_bartender_cents: centsToDollarsInput(initial.second_bartender_cents),
      consumables_cents: centsToDollarsInput(initial.consumables_cents),
      shooter_cents: centsToDollarsInput(initial.shooter_cents),
      fuel_cents: centsToDollarsInput(initial.fuel_cents),
      other_cents: centsToDollarsInput(initial.other_cents),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  async function handleSave() {
    try {
      await setCosts.mutateAsync({
        eventId,
        second_bartender_cents: dollarsToCents(values.second_bartender_cents),
        consumables_cents: dollarsToCents(values.consumables_cents),
        shooter_cents: dollarsToCents(values.shooter_cents),
        fuel_cents: dollarsToCents(values.fuel_cents),
        other_cents: dollarsToCents(values.other_cents),
      });
      toastSuccess("Event costs saved");
    } catch (err) {
      toastError("Couldn't save event costs", getErrorMessage(err));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Event costs</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {COST_FIELDS.map(({ key, label }) => (
            <label key={key} className="block space-y-1">
              <span className="text-xs font-medium text-text-secondary">{label}</span>
              <div className="flex items-center gap-1">
                <span className="text-sm text-text-muted">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label={label}
                  value={values[key]}
                  onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  className="h-9 w-full rounded-md border border-border bg-bg-card px-2 text-sm"
                />
              </div>
            </label>
          ))}
        </div>
        <Button size="sm" onClick={() => void handleSave()} disabled={setCosts.isPending}>
          {setCosts.isPending ? "Saving..." : "Save costs"}
        </Button>
      </CardContent>
    </Card>
  );
}

interface ContributionCardProps {
  contribution: {
    revenue_cents: number;
    total_costs_cents: number;
    contribution_cents: number;
    target_cents: number;
    delta_cents: number;
  };
}

function ContributionCard({ contribution }: ContributionCardProps) {
  const { revenue_cents, total_costs_cents, contribution_cents, target_cents, delta_cents } = contribution;

  const onOrOverTarget = contribution_cents >= target_cents;
  const label = contribution_cents === target_cents ? "On target" : onOrOverTarget ? "Over target" : "Under target";
  const badgeVariant = onOrOverTarget ? "success" : contribution_cents < 0 ? "danger" : "warning";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contribution</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-text-secondary">Revenue</dt>
            <dd className="font-medium text-text-primary" data-testid="contribution-revenue">
              {centsToUsd(revenue_cents)}
            </dd>
          </div>
          <div>
            <dt className="text-text-secondary">Total costs</dt>
            <dd className="font-medium text-text-primary" data-testid="contribution-total-costs">
              {centsToUsd(total_costs_cents)}
            </dd>
          </div>
        </dl>

        <div className="border-t border-border pt-4">
          <p className="text-xs font-medium text-text-secondary">Contribution</p>
          <p className="font-display text-3xl font-bold text-text-primary" data-testid="contribution-amount">
            {centsToUsd(contribution_cents)}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <Badge variant={badgeVariant} data-testid="contribution-target-badge">
              {label}
            </Badge>
            <span className="text-xs text-text-muted" data-testid="contribution-delta">
              {centsToUsd(delta_cents)} vs {centsToUsd(target_cents)} target
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
